import Vue from 'vue';
import { USER } from '@/store/types';
import { showToast } from '@/store/utils';
import { PrivateRoomOperation, newPrivateRoomOperation, privateOperationKey, isDefinitePrivateFailure } from '@/utils/private-room-operation';
import { privateRoomError } from '@/utils/private-room';
import * as api from '@/api/private-room';
import { PrivateRoom } from '@/api/types/private-room';
import { StoreSecretState } from './index';

export interface StorePrivateRoomsState {
  rooms: Record<string, PrivateRoom>;
  revision: number;
  busy: Record<string, boolean>;
  unresolved: Record<string, PrivateRoomOperation | null>;
}
export default {
  namespaced: true,
  state: { rooms: {}, revision: 0, busy: {}, unresolved: {} },
  mutations: {
    busy(state: StorePrivateRoomsState, { key, value }) { Vue.set(state.busy, key, value); },
    unresolved(state: StorePrivateRoomsState, { key, value }) { Vue.set(state.unresolved, key, value); },
    detail(state: StorePrivateRoomsState, room: PrivateRoom) { Vue.set(state.rooms, room.id, room); },
    invalidate(state: StorePrivateRoomsState, content: StoreSecretState) {
      content.list.cache = [];
      content.post.cache = [];
      content.comment.post.cache = [];
      content.room.cacheRooms = [];
      content.room.cacheRoomsAll = [];
      content.filter.info.cache = [];
      state.rooms = {};
      state.revision += 1;
    },
  },
  actions: {
    async detail({ commit, state, rootState }, { id }) {
      const uid = rootState.user.user && rootState.user.user.id;
      const revision = state.revision;
      const res = await api.getPrivateRoom(id);
      if (uid !== (rootState.user.user && rootState.user.user.id) || revision !== state.revision) {
        throw new Error('身份或权益已变化，请刷新');
      }
      const leftOrder = localStorage.getItem(`private-room-left/${uid}/${id}`);
      if (leftOrder && Number(leftOrder) === res.data.orderId) {
        // Do not let a lagging replica resurrect the cycle we just left.
        res.data.joined = false;
        res.data.canPost = false;
        res.data.canComment = false;
      }
      commit('detail', res.data);
      return res.data;
    },
    async list({ rootState }, { mine = false, point = void 0 } = {}) {
      const uid = rootState.user.user && rootState.user.user.id;
      const res = await api.getPrivateRooms(mine, point);
      if (uid !== (rootState.user.user && rootState.user.user.id)) { throw new Error('账号已切换，请刷新'); }
      if (!Array.isArray(res.data)) { throw new Error('私版列表格式无效'); }
      return { items: res.data, nextPoint: res.extra && res.extra.nextPoint };
    },
    async hasJoined({ dispatch }, { point = void 0, seen = [] }: { point?: number; seen?: number[] } = {}) {
      const page = await dispatch('list', { mine: true, point });
      if (page.items.some(room => room.joined && room.memberState === 'joined')) { return true; }
      if (!page.nextPoint || seen.includes(page.nextPoint)) { return false; }
      return dispatch('hasJoined', { point: page.nextPoint, seen: seen.concat(page.nextPoint) });
    },
    invalidate({ commit, rootState }) {
      commit('invalidate', rootState.secret);
    },
    restoreOperation({ commit, rootState }, { id }) {
      const uid = rootState.user.user && rootState.user.user.id;
      const key = privateOperationKey(uid, id);
      const value = JSON.parse(localStorage.getItem(key) || 'null');
      if (value && (value.uid !== uid || value.id !== Number(id))) { throw new Error('未决请求身份不符'); }
      commit('unresolved', { key, value });
      return value;
    },
    async members({ rootState }, { id, state, point }) {
      const uid = rootState.user.user && rootState.user.user.id;
      const revision = rootState.secret.privateRooms.revision;
      const res = await api.getPrivateMembers(id, state, point);
      if (uid !== (rootState.user.user && rootState.user.user.id) || revision !== rootState.secret.privateRooms.revision) {
        throw new Error('账号或权益已变化，请刷新');
      }
      if (!Array.isArray(res.data)) { throw new Error('成员列表格式无效'); }
      return { items: res.data, nextPoint: res.extra && res.extra.nextPoint };
    },
    async sync({ dispatch }, { id }) {
      await dispatch('invalidate');
      await Promise.all([
        dispatch('detail', { id }),
        dispatch(`user/${USER.GET}`, { action: 'refresh' }, { root: true }),
      ]);
    },
    async operate({ state, commit, dispatch, rootState }, params) {
      const uid = rootState.user.user && rootState.user.user.id;
      if (!uid) { throw new Error('请先登录'); }
      const id = Number(params.id);
      const key = privateOperationKey(uid, id);
      if (state.busy[key]) { return { busy: true }; }
      commit('busy', { key, value: true });
      try {
        const pending = await dispatch('restoreOperation', { id });
        if (pending && !params.retry) { throw new Error('请先核对或重试未决操作'); }
        if (params.retry && !pending) { throw new Error('没有可重试的操作'); }
        const operation: PrivateRoomOperation = pending || newPrivateRoomOperation({
          id, uid, kind: params.kind, userId: params.userId, body: params.body || {},
        });
        if (operation.kind !== 'join' && !operation.body.orderId) { throw new Error('当前周期订单不存在，请刷新权益'); }
        // Persist before sending: a reload or lost response must not create another charge.
        localStorage.setItem(key, JSON.stringify(operation));
        commit('unresolved', { key, value: operation });
        let result;
        try {
          const { body, kind, userId } = operation;
          if (kind === 'join') {
            result = await api.joinPrivateRoom(id, body);
          } else if (kind === 'leave') {
            result = await api.leavePrivateRoom(id, body);
          } else if (kind === 'prepay') {
            result = await api.prepayPrivateRoom(id, body);
          } else if (kind === 'autoRenew') {
            result = await api.updatePrivateRenewal(id, body);
          } else if (kind === 'moderate' && userId) {
            result = await api.moderatePrivateMember(id, userId, body);
          } else { throw new Error('操作类型无效'); }
        } catch (error) {
          if (isDefinitePrivateFailure(error)) {
            localStorage.removeItem(key);
            commit('unresolved', { key, value: null });
          }
          if (uid === (rootState.user.user && rootState.user.user.id)) {
            showToast({ text: isDefinitePrivateFailure(error) ? privateRoomError(error) : '结果尚未确认，正在核对权益；可重试原请求', time: 3000 });
            await dispatch('sync', { id }).catch(() => {});
          }
          return { uncertain: !isDefinitePrivateFailure(error), failed: true };
        }
        localStorage.removeItem(key);
        commit('unresolved', { key, value: null });
        if (uid !== (rootState.user.user && rootState.user.user.id)) { return { changedAccount: true }; }
        if (operation.kind === 'leave') {
          localStorage.setItem(`private-room-left/${uid}/${id}`, String(operation.body.orderId));
        } else if (operation.kind === 'join') {
          localStorage.removeItem(`private-room-left/${uid}/${id}`);
        }
        showToast({ text: operation.kind === 'prepay' ? '下一期已预付' : '操作成功', time: 2200 });
        await dispatch('sync', { id }).catch(() => {
          showToast({ text: '操作已成功，权益刷新失败，请刷新核对', time: 3000 });
        });
        return { success: true, kind: operation.kind, data: result.data };
      } finally { commit('busy', { key, value: false }); }
    },
    async settings({ dispatch }, { id, body }) {
      const res = await api.updatePrivateRoom(id, body);
      await dispatch('sync', { id });
      return res.data;
    },
  },
};
